import React, { FC, useMemo, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import {
  setAudioInputDeviceId,
  setAudioOutputDeviceId,
} from "@store/avatarSlice";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";
import { useMediaDeviceList } from "@hooks/useMediaDeviceList";
import { browserSupportsSetSinkId } from "@utils/media-output";

const ConversationAudioOptionsDialog: FC<{
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const audioInputDeviceId = useSelector(
    (s: RootState) => s.avatar.audioInputDeviceId,
  );
  const audioOutputDeviceId = useSelector(
    (s: RootState) => s.avatar.audioOutputDeviceId,
  );
  const { audioInputs, audioOutputs, refresh } = useMediaDeviceList();
  const outputRoutingSupported = useMemo(() => browserSupportsSetSinkId(), []);

  useEffect(() => {
    if (open) {
      void refresh();
    }
  }, [open, refresh]);

  const handleRefresh = useCallback(() => {
    void refresh();
  }, [refresh]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t("companion.audioOptions.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="companion-audio-input-label">
              {t("companion.audioOptions.microphone")}
            </InputLabel>
            <Select
              labelId="companion-audio-input-label"
              label={t("companion.audioOptions.microphone")}
              value={audioInputDeviceId || ""}
              onChange={(e) =>
                dispatch(setAudioInputDeviceId(e.target.value as string))
              }
            >
              <MenuItem value="">
                {t("companion.audioOptions.defaultDevice")}
              </MenuItem>
              {audioInputs.map((d) => (
                <MenuItem key={d.deviceId} value={d.deviceId}>
                  {d.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            fullWidth
            size="small"
            disabled={!outputRoutingSupported}
          >
            <InputLabel id="companion-audio-output-label">
              {t("companion.audioOptions.speaker")}
            </InputLabel>
            <Select
              labelId="companion-audio-output-label"
              label={t("companion.audioOptions.speaker")}
              value={audioOutputDeviceId || ""}
              onChange={(e) =>
                dispatch(setAudioOutputDeviceId(e.target.value as string))
              }
            >
              <MenuItem value="">
                {t("companion.audioOptions.defaultDevice")}
              </MenuItem>
              {audioOutputs.map((d) => (
                <MenuItem key={d.deviceId} value={d.deviceId}>
                  {d.label}
                </MenuItem>
              ))}
            </Select>
            {!outputRoutingSupported ? (
              <FormHelperText>
                {t("companion.audioOptions.outputNotSupported")}
              </FormHelperText>
            ) : null}
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleRefresh}>
          {t("companion.audioOptions.refreshDevices")}
        </Button>
        <Button variant="contained" onClick={onClose}>
          {t("companion.audioOptions.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConversationAudioOptionsDialog;
